import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminListHosts, adminApproveKyc, adminRejectKyc } from '../../api/adminHosts';
import { useToast } from '../../components/ToastContext';

const AdminHostKyc = ({ token }) => {
  const toast = useToast();
  const [hosts, setHosts] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminListHosts(token, { kyc_status: status, limit: 50 });
      setHosts(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load KYC list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id) => {
    try {
      await adminApproveKyc(token, id);
      toast.success('KYC approved');
      load();
    } catch (err) {
      toast.error(err.message || 'Approve failed');
    }
  };
  const reject = async (id) => {
    try {
      await adminRejectKyc(token, id, { reason: 'Rejected by admin' });
      toast.success('KYC rejected');
      load();
    } catch (err) {
      toast.error(err.message || 'Reject failed');
    }
  };

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Hosts / Partners</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">KYC / Verification</h1>
          <p className="text-sm text-slate-600">Review host identity submissions.</p>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        >
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {error && <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">KYC Status</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && hosts.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-slate-500">
                  No hosts found.
                </td>
              </tr>
            )}
            {hosts.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{h.full_name}</td>
                <td className="px-4 py-3 text-slate-700">
                  {h.phone}
                  <div className="text-xs text-slate-500">{h.email}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                    {(h.kyc_status || 'not_submitted').toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <Link
                    to={`/admin/hosts/${h.id}`}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    onClick={() => approve(h.id)}
                    className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(h.id)}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminHostKyc;
